import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, Typography } from "@mui/material";

interface MultiLevelOption {
    id: number;
    choice: string;
    field_label: string;
    [key: string]: any; // for level2, level3, etc.
}

interface MultiLevelField {
    id: number;
    field_type: string;
    label: string;
    multiple_level: MultiLevelOption[];
    field_label?: string;
    is_required?: boolean;
}

interface MultiLevelDropdownProps {
    field: MultiLevelField;
    value?: string | null;
    onFinalSelect?: (lastChoice: MultiLevelOption, path?: string) => void;
    className?: string;
    error?: string | null;
}

function getNextLevelKey(option: MultiLevelOption): string | null {
    if (!option) return null;
    const keys = Object.keys(option);
    const levelKey = keys.find((k) => k.startsWith("level") && Array.isArray(option[k]));
    return levelKey || null;
}

export const MultiLevelDropdown: React.FC<MultiLevelDropdownProps> = ({ field, value, onFinalSelect, className, error }) => {
    const [selected, setSelected] = useState<MultiLevelOption[]>([]);
    const [currentLevels, setCurrentLevels] = useState<MultiLevelOption[][]>([]);

    // Initialize the component with the first level
    useEffect(() => {
        if (field.multiple_level) {
            setCurrentLevels([field.multiple_level]);
        }
    }, [field.multiple_level]);

    // Handle initial value
    useEffect(() => {
        // If value is cleared, reset the internal selection
        if (!value) {
            setSelected([]);
            if (field.multiple_level) {
                setCurrentLevels([field.multiple_level]);
            }
            return;
        }
        if (value && typeof value === 'string' && field.multiple_level) {
            const path = value.split('>');
            let currentOptions = field.multiple_level;
            const newSelected: MultiLevelOption[] = [];
            const newLevels = [field.multiple_level];

            for (let i = 0; i < path.length; i++) {
                const choice = path[i].trim(); // handle accidental spaces
                const found = currentOptions.find((opt) => opt.choice === choice);
                if (found) {
                    newSelected.push(found);
                    const nextLevelKey = getNextLevelKey(found);
                    if (nextLevelKey && found[nextLevelKey]) {
                        currentOptions = found[nextLevelKey];
                        newLevels.push(currentOptions);
                    } else {
                        break;
                    }
                } else {
                    break;
                }
            }
            setSelected(newSelected);
            setCurrentLevels(newLevels);
        }
    }, [value, field.multiple_level]);

    const handleSelect = (option: MultiLevelOption, levelIndex: number) => {
        const newSelected = selected.slice(0, levelIndex);
        const newLevels = currentLevels.slice(0, levelIndex + 1);
        
        newSelected[levelIndex] = option;
        
        const nextLevelKey = getNextLevelKey(option);
        if (nextLevelKey && option[nextLevelKey]) {
            newLevels.push(option[nextLevelKey]);
        }
        
        setSelected(newSelected);
        setCurrentLevels(newLevels);
        
        // Build the full path string
        const path = [...newSelected.slice(0, levelIndex), option].map(item => item.choice).join('>');
        
        // If there are no more levels, call onFinalSelect with the last selected option and the full path
        if (!nextLevelKey || !option[nextLevelKey] || option[nextLevelKey].length === 0) {
            onFinalSelect?.(option, path);
        }
    };
      
    const renderDropdowns = () => {
        if (!currentLevels || currentLevels.length === 0) return null;

        return currentLevels.map((options, index) => {
            if (!options || options.length === 0) return null;
            
            // Get the field label from the first option's field_label if available
            const fieldLabel = options[0]?.field_label || `Option ${index + 1}`;
            const isRequired = index === 0 && field.is_required; // Only show required for first level if field is required
            
            return (
                <div key={index} className={className}>
                    {fieldLabel && (
                        <Typography variant="subtitle2" style={{ marginLeft: '4px' }}>
                            {fieldLabel} {isRequired && <span style={{ color: 'red' }}>*</span>}
                        </Typography>
                    )}
                    <Autocomplete
                        fullWidth
                        options={options}
                        value={selected[index] || null}
                        getOptionLabel={(option) => option?.choice || ''}
                        isOptionEqualToValue={(option, val) => option.id === val.id}
                        filterOptions={(opts, { inputValue }) => {
                            const query = inputValue.trim().toLowerCase();
                            if (!query) return opts;
                            return opts.filter((opt) =>
                                opt.choice?.toLowerCase().includes(query)
                            );
                        }}
                        onChange={(_e, newValue) => {
                            if (newValue) {
                                handleSelect(newValue, index);
                            }
                        }}
                        disabled={!options || options.length === 0}
                        noOptionsText="No matching options"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={`Search / Select ${fieldLabel}`}
                                error={Boolean(error && index === 0)}
                            />
                        )}
                    />
                    {error && index === 0 && (
                        <div style={{ 
                            color: 'red', 
                            marginTop: '4px',
                            marginLeft: '4px'
                        }}>
                            {error}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
       <>
            {renderDropdowns()}
       </>
    );
};